import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, Trash2, Loader2 } from "lucide-react";

function EditableRow({ name, onRename, onDelete, testGroup }) {
  const [val, setVal] = useState(name);
  const [busy, setBusy] = useState(false);
  const changed = val.trim() && val.trim() !== name;

  const doRename = async () => {
    setBusy(true);
    await onRename(name, val.trim());
    setBusy(false);
  };
  const doDelete = async () => {
    setBusy(true);
    await onDelete(name);
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Input value={val} onChange={(e) => setVal(e.target.value)}
        data-testid={`manage-${testGroup}-input-${name}`}
        className="h-10 bg-white border-[#E2E2E2]" />
      <Button type="button" size="icon" disabled={!changed || busy} onClick={doRename}
        data-testid={`manage-${testGroup}-save-${name}`}
        className="h-10 w-10 shrink-0 bg-[#E6017D] hover:bg-[#C4016A] text-white disabled:opacity-40">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
      </Button>
      <Button type="button" variant="ghost" size="icon" disabled={busy} onClick={doDelete}
        data-testid={`manage-${testGroup}-delete-${name}`}
        className="h-10 w-10 shrink-0 text-[#5F6368] hover:text-[#D11A2A] hover:bg-[#D11A2A]/10">
        <Trash2 size={16} />
      </Button>
    </div>
  );
}

function ListBody({ items, emptyText, onRename, onDelete, testGroup }) {
  if (items.length === 0) {
    return <p className="text-sm text-[#5F6368] py-6 text-center">{emptyText}</p>;
  }
  return (
    <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
      {items.map((n) => (
        <EditableRow key={n} name={n} onRename={onRename} onDelete={onDelete} testGroup={testGroup} />
      ))}
    </div>
  );
}

export default function ManageListsDialog({
  open, onOpenChange, helpers, workTypes,
  onRenameHelper, onDeleteHelper, onRenameWorkType, onDeleteWorkType,
}) {
  const tabTriggerCls =
    "data-[state=active]:bg-[#E6017D] data-[state=active]:text-white text-[#5F6368] rounded-md";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E2E2E2] max-w-lg" data-testid="manage-lists-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#E6017D]">Listen verwalten</DialogTitle>
          <DialogDescription className="text-[#5F6368]">
            Helfer und Bauarbeiten korrigieren oder entfernen. Umbenennungen werden in allen Einträgen übernommen.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="helpers" className="mt-2">
          <TabsList className="grid grid-cols-2 w-full bg-[#F4F4F4]">
            <TabsTrigger value="helpers" className={tabTriggerCls} data-testid="manage-tab-helpers">
              Helfer ({helpers.length})
            </TabsTrigger>
            <TabsTrigger value="worktypes" className={tabTriggerCls} data-testid="manage-tab-worktypes">
              Bauarbeiten ({workTypes.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="helpers" className="mt-4">
            <ListBody items={helpers} emptyText="Noch keine gespeicherten Helfer."
              onRename={onRenameHelper} onDelete={onDeleteHelper} testGroup="helper" />
          </TabsContent>

          <TabsContent value="worktypes" className="mt-4">
            <ListBody items={workTypes} emptyText="Noch keine Bauarbeiten."
              onRename={onRenameWorkType} onDelete={onDeleteWorkType} testGroup="worktype" />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
